import {
  CarouselLayout,
  FocusLayout,
  FocusLayoutContainer,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
} from "@livekit/components-react";
import { isTrackReference } from "@livekit/components-core";
import { Track } from "livekit-client";
import { type ReactNode } from "react";

interface FocusVideoConferenceProps {
  focusedContent?: ReactNode;
}

/**
 * Focused room layout that prioritizes screen-share and falls back to camera.
 */
export const FocusVideoConference = ({
  focusedContent,
}: FocusVideoConferenceProps) => {
  const isMobile = window.innerWidth < 768;
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const focusedTrack =
    tracks.find(
      (track) =>
        isTrackReference(track) &&
        track.publication.source === Track.Source.ScreenShare &&
        track.publication.isSubscribed,
    ) ??
    tracks.find((track) => !track.participant.isLocal) ??
    null;

  const carouselTracks = tracks.filter((track) => track !== focusedTrack);

  if (isMobile) {
    if (focusedContent) {
      return (
        <>
          <RoomAudioRenderer />
          <div className="w-full h-full rounded-md">{focusedContent}</div>
        </>
      );
    }
    return (
      <>
        <RoomAudioRenderer />
        <GridLayout tracks={tracks}>
          <ParticipantTile />
        </GridLayout>
      </>
    );
  }

  if (focusedContent) {
    return (
      <>
        <RoomAudioRenderer />
        <FocusLayoutContainer>
          <CarouselLayout tracks={tracks}>
            <ParticipantTile />
          </CarouselLayout>
          <div className="w-full h-full rounded-md">{focusedContent}</div>
        </FocusLayoutContainer>
      </>
    );
  }

  return (
    <>
      <RoomAudioRenderer />
      {!focusedTrack ? (
        <GridLayout tracks={tracks}>
          <ParticipantTile />
        </GridLayout>
      ) : (
        <FocusLayoutContainer>
          <CarouselLayout tracks={carouselTracks}>
            <ParticipantTile />
          </CarouselLayout>
          <FocusLayout trackRef={focusedTrack} />
        </FocusLayoutContainer>
      )}
    </>
  );
};
